import axios from 'axios';
import type {
  LoginResponse, ChallengeResponse, MeProfileResponse, ChallengeInfo, PasswordPolicyResponse,
  MfaOptionsResponse, MfaEnrollTotpResponse,
} from '@/types/rbac-auth.types';

const AUTH = import.meta.env.VITE_API_AUTH_BASE_URL;   // p.ej. /rbac  (A: confirmar para credit)
const RBAC = import.meta.env.VITE_API_RBAC_BASE_URL;   // p.ej. /rbac-backoffice (A)
const TENANT_ID = import.meta.env.VITE_TENANT_ID;      // 'credit' (A)
const BASE = import.meta.env.VITE_API_URL;

// cliente dedicado con cookies (no reusa el interceptor de negocio para evitar recursión de refresh)
const authClient = axios.create({ baseURL: BASE, withCredentials: true, timeout: 20000 });
const unwrap = <T>(r: { data?: T }): T => (r && typeof r === 'object' && 'data' in r ? (r.data as T) : (r as T));

export async function login(identifier: string, password: string): Promise<LoginResponse> {
  const res = await authClient.post(`${AUTH}/auth/login`, { identifier, email: identifier, password, tenantId: TENANT_ID });
  return unwrap<LoginResponse>(res.data);
}

export async function challenge(body: { email: string; session: string; challengeType: ChallengeInfo['challengeType']; challengeResponses: Record<string, string> }): Promise<ChallengeResponse> {
  const res = await authClient.post(`${AUTH}/auth/challenge`, { ...body, identifier: body.email, tenantId: TENANT_ID });
  return unwrap<ChallengeResponse>(res.data);
}

export async function logout(): Promise<void> {
  await authClient.post(`${AUTH}/auth/logout`, {});
}

export async function refresh(): Promise<boolean> {
  try { await authClient.post(`${AUTH}/auth/refresh`, {}); return true; } catch { return false; }
}

export async function getProfile(): Promise<MeProfileResponse> {
  const res = await authClient.get(`${RBAC}/me/profile`);
  return unwrap<MeProfileResponse>(res.data);
}

/** Forgot-password paso 1: envía OTP al correo. Anti-enumeración: SIEMPRE 200. */
export async function requestPasswordReset(identifier: string): Promise<void> {
  await authClient.post(`${AUTH}/auth/request-password-reset`, {
    identifier, email: identifier, tenantId: TENANT_ID,
  });
}

/** Forgot-password paso 2: verifica el OTP y devuelve un resetToken de un solo uso. */
export async function verifyPasswordResetOtp(identifier: string, otpCode: string): Promise<{ resetToken: string }> {
  const res = await authClient.post(`${AUTH}/auth/verify-password-reset-otp`, {
    identifier, email: identifier, otpCode, tenantId: TENANT_ID,
  });
  return unwrap<{ resetToken: string }>(res.data);
}

/** Forgot-password paso 3: setea la contraseña nueva con el resetToken. */
export async function confirmPasswordReset(identifier: string, resetToken: string, newPassword: string): Promise<void> {
  await authClient.post(`${AUTH}/auth/confirm-password-reset`, {
    identifier, email: identifier, resetToken, newPassword, tenantId: TENANT_ID,
  });
}

/** Reenvío del OTP de LOGIN. Devuelve una session nueva que hay que adoptar. Puede no estar desplegado. */
export async function resendLoginOtp(identifier: string): Promise<{ session?: string }> {
  const res = await authClient.post(`${AUTH}/auth/resend-otp`, {
    identifier, email: identifier, tenantId: TENANT_ID,
  });
  return unwrap<{ session?: string }>(res.data);
}

/**
 * @deprecated Devuelve la policy GLOBAL del lambda RBAC, no la del pool de `une`. El front usa la
 * constante fija de `@/lib/passwordRules`. Se reactiva cuando el RBAC exponga policy per-tenant.
 */
export async function getPasswordPolicy(): Promise<PasswordPolicyResponse> {
  const res = await authClient.get(`${AUTH}/public/password-policy`);
  return unwrap<PasswordPolicyResponse>(res.data);
}

export async function getMfaOptions(): Promise<MfaOptionsResponse> {
  const res = await authClient.get(`${RBAC}/me/security/mfa/options`);
  return unwrap<MfaOptionsResponse>(res.data);
}

export async function chooseEmailMfa(): Promise<{ status: string }> {
  const res = await authClient.post(`${RBAC}/mfa/choose-email`, {});
  return unwrap<{ status: string }>(res.data);
}

export async function chooseSmsMfa(): Promise<{ status: string }> {
  const res = await authClient.post(`${RBAC}/mfa/choose-sms`, {});
  return unwrap<{ status: string }>(res.data);
}

export async function enrollTotp(): Promise<MfaEnrollTotpResponse> {
  const res = await authClient.post(`${RBAC}/mfa/enroll-totp`, {});
  return unwrap<MfaEnrollTotpResponse>(res.data);
}

export async function verifyTotpEnrollment(totpCode: string, enrollmentToken: string): Promise<{ enrolled: boolean }> {
  const res = await authClient.post(`${RBAC}/mfa/verify-totp-enrollment`, { totpCode, enrollmentToken });
  return unwrap<{ enrolled: boolean }>(res.data);
}
